import { useEffect, useState } from 'react';
import BlogNavigation from '../blog/components/BlogNavigation';
import PrivacyContent from './components/PrivacyContent';
import Footer from '../home/components/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://jobos.dev';

export default function PrivacyPolicyPage() {
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    document.title = 'Privacy Policy — JobOS | Canada-Based Data Protection';

    const setMeta = (name: string, content: string, prop = false) => {
      const attr = prop ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', 'JobOS Privacy Policy. Learn how we collect, use, and protect your personal data under Canadian law (PIPEDA & CASL). We never sell your data.');
    setMeta('robots', 'index, follow');
    setMeta('og:title', 'Privacy Policy — JobOS', true);
    setMeta('og:url', `${SITE_URL}/privacy`, true);
    setMeta('og:type', 'website', true);

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${SITE_URL}/privacy`);

    document.querySelectorAll('script[data-schema-page="privacy"]').forEach(el => el.remove());
    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'Privacy Policy — JobOS',
        url: `${SITE_URL}/privacy`,
        description: 'JobOS Privacy Policy. Learn how we collect, use, and protect your personal data.',
        publisher: { '@type': 'Organization', name: 'JobOS', url: SITE_URL },
        inLanguage: 'en-US',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Privacy Policy', item: `${SITE_URL}/privacy` },
        ],
      },
    ];
    schemas.forEach(schema => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-schema-page', 'privacy');
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      document.querySelectorAll('script[data-schema-page="privacy"]').forEach(el => el.remove());
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      setScrollProgress(docHeight > 0 ? (window.scrollY / docHeight) * 100 : 0);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0E1A] text-gray-900 dark:text-[#F8FAFC]">
      {/* Reading progress bar */}
      <div className="fixed top-0 left-0 w-full h-1 bg-gray-200 dark:bg-gray-800 z-[60]">
        <div
          className="h-full bg-gradient-to-r from-[#6366F1] to-[#818CF8] transition-all duration-150"
          style={{ width: `${scrollProgress}%` }}
        />
      </div>

      <BlogNavigation />

      {/* Hero */}
      <div className="relative h-[400px] w-full overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/e2c0bd808afa22c8e115c8c80d614ffb.jpg"
          alt="Privacy Policy Hero"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/60"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="max-w-4xl mx-auto px-6 text-center w-full">
            <div className="inline-block px-4 py-1.5 bg-[#6366F1]/20 backdrop-blur-sm border border-[#6366F1]/30 rounded-full mb-5">
              <span className="text-[#818CF8] text-sm font-medium">Legal & Compliance</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-black text-white mb-4 leading-tight tracking-tighter">
              Privacy Policy
            </h1>
            <p className="text-lg text-gray-200 max-w-xl mx-auto font-medium leading-relaxed">
              We're committed to protecting your privacy under Canadian federal and provincial law.
            </p>
          </div>
        </div>
      </div>

      {/* Meta bar */}
      <div className="w-full border-b border-gray-100 dark:border-white/5 bg-white dark:bg-[#0A0E1A]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <i className="ri-refresh-line text-[#6366F1]"></i>
            <span className="font-medium text-gray-700 dark:text-gray-300">Last updated:</span>
            <time dateTime="2026-03-05" className="font-mono">March 5, 2026</time>
          </div>
          <div className="w-px h-4 bg-gray-200 dark:bg-white/10 hidden sm:block"></div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-xs font-bold">
              <i className="ri-shield-check-line text-xs"></i>
              PIPEDA Compliant
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#6366F1]/10 border border-[#6366F1]/20 text-[#6366F1] dark:text-[#818CF8] text-xs font-bold">
              <i className="ri-mail-check-line text-xs"></i>
              CASL Compliant
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-600 dark:text-amber-400 text-xs font-bold">
              <i className="ri-flag-line text-xs"></i>
              Canada-Based
            </span>
          </div>
          <div className="ml-auto text-xs text-gray-400 dark:text-gray-500 font-mono hidden md:block">
            Effective Date: March 5, 2026
          </div>
        </div>
      </div>

      {/* Main content */}
      <main>
        <PrivacyContent />
      </main>

      <Footer />
    </div>
  );
}
